/**
 * Lightweight Charts (TradingView, Apache-2.0) view for the DEMA-WMA Gainer comparison:
 *   pane 0  index 5-minute candles + DEMA fast / slow + backtest & paper markers;
 *   pane 1  RSI and its WMA (WMA coloured by slope: green rising, red falling);
 *   pane 2  cumulative index points: backtest trades, paper trades, DEMA-trend baseline.
 * Presentational only (data comes mapped from `demaWmaChartData.ts`). The library's TradingView
 * attribution logo is left enabled.
 */
import { useEffect, useMemo, useRef } from "react";
import type { ComponentRef } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CandlestickSeries, ColorType, LineSeries, createChart, createSeriesMarkers } from "lightweight-charts";
import type { IChartApi, ISeriesApi, ISeriesMarkersPluginApi, MouseEventParams, Time } from "lightweight-charts";
import { CANDLE_DOWN_COLOR, CANDLE_UP_COLOR } from "@/components/charts/candlestickChartData";
import { IST_ZONE, formatZonedDateTime, formatZonedTick } from "@/components/charts/zonedTime";
import { COLORS, type ChartData, type Marker } from "./demaWmaChartData";

export interface DemaWmaComparisonChartProps {
  data: ChartData;
  markers: readonly Marker[];
  labels: { demaFast: string; demaSlow: string; rsi: string; wma: string };
  showBacktestPoints: boolean;
  height?: number;
  /** Epoch seconds of a bar to bring into view (e.g. a clicked trade). */
  focusTime?: number | null;
  onHoverBar?: (barIndex: number | null) => void;
}

const BAR_SECONDS = 300;

export default function DemaWmaComparisonChart({
  data, markers, labels, showBacktestPoints, height = 640, focusTime = null, onHoverBar,
}: DemaWmaComparisonChartProps) {
  const theme = useTheme();
  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;
  const containerRef = useRef<ComponentRef<"div"> | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<Record<string, ISeriesApi<"Line"> | ISeriesApi<"Candlestick">>>({});
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const hoverRef = useRef(onHoverBar);
  hoverRef.current = onHoverBar;
  const indexByTime = useMemo(() => new Map(data.times.map((t, i) => [t, i])), [data.times]);
  const indexRef = useRef(indexByTime);
  indexRef.current = indexByTime;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const chart = createChart(container, {
      autoSize: true,
      height,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, textColor, fontFamily: theme.typography.fontFamily },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
      rightPriceScale: { borderColor: gridColor },
      timeScale: {
        borderColor: gridColor, timeVisible: true, secondsVisible: false,
        tickMarkFormatter: (t: Time, type: number) => (typeof t === "number" ? formatZonedTick(t, type, IST_ZONE) : null),
      },
      localization: { timeFormatter: (t: Time) => (typeof t === "number" ? formatZonedDateTime(t, IST_ZONE) : String(t)) },
    });
    const s: Record<string, ISeriesApi<"Line"> | ISeriesApi<"Candlestick">> = {};
    s.candles = chart.addSeries(CandlestickSeries, {
      upColor: CANDLE_UP_COLOR, downColor: CANDLE_DOWN_COLOR, borderUpColor: CANDLE_UP_COLOR, borderDownColor: CANDLE_DOWN_COLOR,
      wickUpColor: CANDLE_UP_COLOR, wickDownColor: CANDLE_DOWN_COLOR,
    }, 0);
    const lineOpts = (color: string, title: string, lineWidth: 1 | 2 = 2) =>
      ({ color, title, lineWidth, priceLineVisible: false, lastValueVisible: false });
    s.demaFast = chart.addSeries(LineSeries, lineOpts(COLORS.demaFast, labels.demaFast), 0);
    s.demaSlow = chart.addSeries(LineSeries, lineOpts(COLORS.demaSlow, labels.demaSlow), 0);
    s.rsi = chart.addSeries(LineSeries, lineOpts(COLORS.rsi, labels.rsi, 1), 1);
    s.wma = chart.addSeries(LineSeries, lineOpts(COLORS.wmaFlat, labels.wma), 1);
    s.baseline = chart.addSeries(LineSeries, lineOpts(COLORS.baseline, "DEMA-trend baseline (pts)", 1), 2);
    s.backtestPoints = chart.addSeries(LineSeries, lineOpts(COLORS.backtest, "Backtest trades (pts)"), 2);
    s.paperPoints = chart.addSeries(LineSeries, lineOpts(COLORS.paper, "Paper trades (pts)"), 2);
    const panes = typeof chart.panes === "function" ? chart.panes() : [];
    panes[0]?.setHeight?.(Math.round(height * 0.6));
    panes[1]?.setHeight?.(Math.round(height * 0.18));
    panes[2]?.setHeight?.(Math.round(height * 0.22));
    const onMove = (p: MouseEventParams<Time>) => {
      const t = typeof p.time === "number" ? p.time : null;
      hoverRef.current?.(t === null ? null : indexRef.current.get(t) ?? null);
    };
    chart.subscribeCrosshairMove?.(onMove);
    chartRef.current = chart;
    seriesRef.current = s;
    return () => {
      chart.unsubscribeCrosshairMove?.(onMove);
      chart.remove();
      chartRef.current = null;
      seriesRef.current = {};
      markersRef.current = null;
    };
    // Created once; data / theme changes are applied by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const s = seriesRef.current;
    if (!s.candles) return;
    const cast = <T extends { time: number }>(xs: readonly T[]) => xs.map((x) => ({ ...x, time: x.time as Time }));
    s.candles.setData(cast(data.candles));
    s.demaFast.setData(cast(data.demaFast));
    s.demaSlow.setData(cast(data.demaSlow));
    s.rsi.setData(cast(data.rsi));
    s.wma.setData(cast(data.wma));
    s.baseline.setData(cast(data.baseline));
    s.backtestPoints.setData(showBacktestPoints ? cast(data.backtestPoints) : []);
    s.paperPoints.setData(cast(data.paperPoints));
    chartRef.current?.timeScale().fitContent();
  }, [data, showBacktestPoints]);

  useEffect(() => {
    const candles = seriesRef.current.candles;
    if (!candles) return;
    const mapped = markers.map((m) => ({ ...m, time: m.time as Time }));
    if (markersRef.current) markersRef.current.setMarkers(mapped);
    else markersRef.current = createSeriesMarkers(candles, mapped);
  }, [markers]);

  useEffect(() => {
    if (focusTime === null || !chartRef.current) return;
    const ts = chartRef.current.timeScale();
    ts.setVisibleRange?.({ from: (focusTime - 30 * BAR_SECONDS) as Time, to: (focusTime + 30 * BAR_SECONDS) as Time });
  }, [focusTime]);

  useEffect(() => {
    chartRef.current?.applyOptions({
      layout: { textColor },
      grid: { vertLines: { color: gridColor }, horzLines: { color: gridColor } },
    });
  }, [textColor, gridColor]);

  return <Box ref={containerRef} data-testid="dema-compare-chart" sx={{ width: "100%", height }} />;
}
